import React, { useEffect, useState, useMemo } from "react";
import "./tictac.css";

function formatLeaderboardValue(row, field) {
  const v = row[field];
  if (v === undefined || v === null || v === "") return "—";
  return String(v);
}

function LeaderboardTable({ top5, meOutside, currentUserId, showTimeColumn }) {
  const renderRow = (row, isYou) => (
    <tr key={`${row.userid}-${row.rank}`} className={isYou ? "lb-row-you" : ""}>
      <td className={`lb-rank-cell ${row.rank <= 3 ? `lb-rank-cell--${row.rank}` : ""}`}>
        {row.rank}
      </td>
      <td className="lb-name-cell">{row.name || "Player"}</td>
      <td className="lb-score-cell">{formatLeaderboardValue(row, "score")}</td>
      {showTimeColumn && (
        <td className="lb-time-cell">{formatLeaderboardValue(row, "time")}</td>
      )}
    </tr>
  );

  return (
    <div className="lb-table-wrap">
      <table className="lb-table">
        <thead>
          <tr>
            <th>Rank</th>
            <th>Name</th>
            <th>Points</th>
            {showTimeColumn && <th>Time</th>}
          </tr>
        </thead>
        <tbody>
          {top5.map((row) =>
            renderRow(
              row,
              currentUserId !== "" && String(row.userid) === String(currentUserId)
            )
          )}
          {meOutside && (
            <>
              <tr className="lb-separator-row">
                <td colSpan={showTimeColumn ? 4 : 3} />
              </tr>
              {renderRow(meOutside, true)}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ThankYou({ updateHeader }) {
  const [userData, setUserData] = useState(null);
  const [reports, setReports] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [leaderboardError, setLeaderboardError] = useState("");
  const [top5, setTop5] = useState([]);
  const [meOutside, setMeOutside] = useState(null);

  useEffect(() => {
    const stored = sessionStorage.getItem("userData");
    if (stored) setUserData(JSON.parse(stored));
  }, []);

  useEffect(() => {
    if (!userData) return;

    updateHeader({
      backButtonRedirect:
        process.env.REACT_APP_BASE_URL + "/game-detail/" + userData.gameId,
    });

    const key = "report-" + userData.sessionId;
    try {
      const report = localStorage.getItem(key);
      if (report) setReports(JSON.parse(report));
    } catch (e) {
      setReports(null);
    }
  }, [userData, updateHeader]);

  useEffect(() => {
    if (!userData?.sessionId) {
      setLeaderboardLoading(false);
      return;
    }

    let cancelled = false;

    const loadLeaderboard = async () => {
      setLeaderboardLoading(true);
      setLeaderboardError("");
      try {
        const res = await fetch(
          process.env.REACT_APP_FOLDER_LINK + "/api/leaderboard.php",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              sessionId: userData.sessionId,
              userId: userData.userId,
              organizationId: userData.organizationId,
            }),
          }
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (!res.ok || !data.success) {
          setLeaderboardError(data.error || "Unable to load leaderboard.");
          setTop5([]);
          setMeOutside(null);
          return;
        }

        setTop5(Array.isArray(data.top5) ? data.top5 : []);
        setMeOutside(data.meOutside ?? null);
      } catch (err) {
        if (!cancelled) {
          setLeaderboardError("Unable to load leaderboard.");
          setTop5([]);
          setMeOutside(null);
        }
      } finally {
        if (!cancelled) setLeaderboardLoading(false);
      }
    };

    loadLeaderboard();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  const showTimeColumn = useMemo(() => {
    const rows = [...top5, ...(meOutside ? [meOutside] : [])];
    return rows.some((r) => r.time);
  }, [top5, meOutside]);

  return (
    <div className="tt-wrapper thankyou-page">
      <div className="thankyou-page-inner">
        {reports && (
          <p className="thankyou-personal-score">
            Your score is <strong>{reports.points}</strong>
            {reports.time ? (
              <>
                {" "}
                in <strong>{reports.time}</strong>
              </>
            ) : null}
          </p>
        )}

        <div className="lb-card">
          <div className="lb-card-header">
            <span className="lb-trophy" aria-hidden="true">
              🏆
            </span>
            <h2 className="lb-title">Leaderboard</h2>
            <span className="lb-trophy" aria-hidden="true">
              🏆
            </span>
          </div>

          {leaderboardLoading && (
            <p className="lb-status">Loading leaderboard...</p>
          )}

          {!leaderboardLoading && leaderboardError && (
            <p className="lb-status lb-status--error">{leaderboardError}</p>
          )}

          {!leaderboardLoading &&
            !leaderboardError &&
            top5.length === 0 && (
              <p className="lb-status">No scores yet for this session.</p>
            )}

          {!leaderboardLoading && !leaderboardError && top5.length > 0 && (
            <LeaderboardTable
              top5={top5}
              meOutside={meOutside}
              currentUserId={userData?.userId || ""}
              showTimeColumn={showTimeColumn}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default ThankYou;
